import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import {
  UsersIcon,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  HelpCircle,
  Eye,
} from 'lucide-react';
import { tenantApi, TenantCRMLead, TenantCRMLeadUpdate } from '../lib/tenant-api';
import { PageLoader, Pagination } from '../components/common';
import Modal from '../components/Modal';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { useNavigate } from 'react-router-dom';

const HUBSPOT_LEAD_SOURCE_HELP_STEPS = [
  "In HubSpot, go to Settings > Properties > Contact properties.",
  "Create a contact property with internal name 'lead_source' or 'leadsource'.",
  "Add an option value named 'LAWMA' and save the property.",
  'Retry sync from this Leads page.',
];

function HubspotLeadSourceHelp() {
  return (
    <details className="relative group">
      <summary
        className="list-none cursor-pointer inline-flex items-center justify-center rounded-full p-0.5 text-amber-700 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
        aria-label="HubSpot sync setup help"
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </summary>
      <div className="absolute left-0 z-20 mt-2 w-80 rounded-md border border-amber-200 bg-white p-3 text-xs text-gray-700 shadow-lg">
        <p className="font-semibold text-amber-800 mb-2">How to fix this HubSpot sync error</p>
        <ol className="list-decimal pl-4 space-y-1">
          {HUBSPOT_LEAD_SOURCE_HELP_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
    </details>
  );
}

export default function Leads() {
  const isHubspotLeadSourceError = (lead: TenantCRMLead) => {
    const error = (lead.error_message || '').toLowerCase();
    return lead.crm_platform === 'hubspot' && (
      error.includes('lead_source')
      || error.includes('leadsource')
      || error.includes('lawma')
      || error.includes('hubspot setup required')
    );
  };

  const [leads, setLeads] = useState<TenantCRMLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // Pagination State
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Edit State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<TenantCRMLead | null>(null);
  const [editFormData, setEditFormData] = useState<TenantCRMLeadUpdate>({});

  // Delete State
  const [leadToDelete, setLeadToDelete] = useState<TenantCRMLead | null>(null);

  // Sync State
  const [syncingLeadId, setSyncingLeadId] = useState<string | null>(null);

  useEffect(() => {
    fetchLeads();
  }, [page]);

  const handleSearch = () => {
    // If we are on page 1, current useEffect logic won't trigger re-fetch by itself if we just setPage(1).
    // So if page is already 1, we must explicitly fetch.
    if (page === 1) {
      fetchLeads();
    } else {
      setPage(1);
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await tenantApi.getLeads(searchTerm, page, 50);
      setLeads(data.items);
      setTotal(data.total);
      setTotalPages(data.pages);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (lead: TenantCRMLead) => {
    if (syncingLeadId) return;

    try {
      setSyncingLeadId(lead.id);
      await tenantApi.syncLead(lead.id);
      // Refresh leads to get updated status
      await fetchLeads();
      toast.success('Lead synced successfully');
    } catch (err: any) {
      console.error('Failed to sync lead:', err);
      toast.error(err.message || 'Failed to sync lead');
    } finally {
      setSyncingLeadId(null);
    }
  };

  const handleDelete = (lead: TenantCRMLead) => {
    setLeadToDelete(lead);
  };

  const confirmDelete = async () => {
    if (!leadToDelete) return;

    try {
      await tenantApi.deleteLead(leadToDelete.id);
      await fetchLeads();
      setLeadToDelete(null);
      toast.success('Lead deleted successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete lead');
    }
  };

  const handleEdit = (lead: TenantCRMLead) => {
    setSelectedLead(lead);
    setEditFormData({
      email: lead.email,
      firstname: lead.firstname,
      lastname: lead.lastname,
      phone: lead.phone,
      company: lead.company,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedLead) return;

    try {
      await tenantApi.updateLead(selectedLead.id, editFormData);
      setIsEditModalOpen(false);
      setSelectedLead(null);
      fetchLeads();
      toast.success('Lead updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update lead');
    }
  };

  const getStatusBadge = (lead: TenantCRMLead) => {
    if (lead.is_synced) {
      return (
        <span className="inline-flex items-center px-2 py-0.5  text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Synced
        </span>
      );
    }

    if (lead.sync_status === 'failed') {
      return (
        <div className="inline-flex items-center gap-1.5" title={lead.error_message}>
          <span className="inline-flex items-center px-2 py-0.5  text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </span>
          {isHubspotLeadSourceError(lead) && <HubspotLeadSourceHelp />}
        </div>
      );
    }

    return (
      <span className="inline-flex items-center px-2 py-0.5  text-xs font-medium bg-gray-100 text-gray-800">
        <Clock className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  const [viewLead, setViewLead] = useState<TenantCRMLead | null>(null);

  const handleView = (lead: TenantCRMLead) => {
    setViewLead(lead);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Leads
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage your captured leads and their CRM status.
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white p-4 shadow-sm border border-gray-200">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300  leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="Search leads by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </div>
        <button
          onClick={handleSearch}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium  text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Search
        </button>
        <button
          onClick={() => {
            setSearchTerm('');
            setPage(1);
            // We need to fetch with empty string. Since setState is async, we can't just call fetchLeads immediately if we rely on state.
            // But fetchLeads uses current searchTerm state.
            // A pattern here is to pass terms to fetchLeads or let effect handle it.
            // Since we removed debouncer, setting page to 1 will trigger effect which reads searchTerm.
            // BUT searchTerm update is async.
            // Better to reload window or just explicit clear?
            // Let's keep it simple: wait for effect or force custom fetch.
            // Due to closure, fetchLeads inside useEffect uses updated state? No.
            // Actually, best to just let the user clear and click search, or implement proper clear.
            // For "Refresh", we just re-fetch current.
            fetchLeads();
          }}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium  text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Leads Table */}
      <div className="bg-white shadow overflow-hidden sm: flex flex-col">
        <div className="overflow-x-auto">
          {loading && leads.length === 0 ? (
            <PageLoader fullScreen={false} />
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No leads found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search terms or wait for new leads to arrive.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CRM Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {lead.firstname || lead.lastname ? `${lead.firstname || ''} ${lead.lastname || ''}`.trim() : '-- --'}
                        </div>
                        {lead.phone && <div className="text-xs text-gray-400">{lead.phone}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{lead.email || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lead.company || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start gap-1.5">
                        {lead.crm_platform && (
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                            {lead.crm_platform}
                          </span>
                        )}
                        {getStatusBadge(lead)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleView(lead)}
                          className="text-gray-600 hover:text-gray-900 p-1  hover:bg-gray-50"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleSync(lead)}
                          disabled={syncingLeadId === lead.id || lead.is_synced}
                          className={`text-indigo-600 hover:text-indigo-900 p-1  hover:bg-indigo-50 ${(syncingLeadId === lead.id || lead.is_synced) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          title="Sync to CRM"
                        >
                          <RefreshCw className={`h-4 w-4 ${syncingLeadId === lead.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleEdit(lead)}
                          className="text-indigo-600 hover:text-indigo-900 p-1  hover:bg-indigo-50"
                          title="Edit Lead"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(lead)}
                          className="text-red-600 hover:text-red-900 p-1  hover:bg-red-50"
                          title="Delete Lead"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {total > 0 && totalPages > 1 && (
          <Pagination
            className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6"
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            rangeStart={(page - 1) * 50 + 1}
            rangeEnd={Math.min(page * 50, total)}
            totalItems={total}
          />
        )}
      </div>

      {/* View Details Modal */}
      <Modal
        isOpen={!!viewLead}
        onClose={() => setViewLead(null)}
        title="Lead Details"
        size="xl"
        footer={
          <button
            type="button"
            className="w-full inline-flex justify-center  border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
            onClick={() => setViewLead(null)}
          >
            Close
          </button>
        }
      >
        {viewLead && (
          <div className="space-y-4 py-2">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Contact Information</h4>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 ">
                <div>
                  <span className="block text-xs text-gray-500">Full Name</span>
                  <span className="block text-sm font-medium text-gray-900">
                    {viewLead.firstname || viewLead.lastname
                      ? `${viewLead.firstname || ''} ${viewLead.lastname || ''}`.trim()
                      : '-- --'}
                  </span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500">Email</span>
                  <span className="block text-sm font-medium text-gray-900">{viewLead.email || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500">Phone</span>
                  <span className="block text-sm font-medium text-gray-900">{viewLead.phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500">Company</span>
                  <span className="block text-sm font-medium text-gray-900">{viewLead.company || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-xs text-gray-500">Date Captured</span>
                  <span className="block text-sm font-medium text-gray-900">
                    {viewLead.created_at ? new Date(viewLead.created_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {viewLead.session_id && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Conversation Session</h4>
                <div className="bg-gray-50 p-3  space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-xs text-gray-500">Session ID</span>
                      <span className="block text-sm font-medium text-gray-900">{viewLead.session_id || 'N/A'}</span>
                    </div>
                    <button
                      onClick={() => navigate(`/conversations?session_id=${viewLead.session_id || ''}`)}
                      className="text-primary-600 text-sm font-medium hover:text-primary-900 flex items-center gap-2"
                    >
                      View Conversation
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Synchronization Status</h4>
              <div className="bg-gray-50 p-3  space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="block text-xs text-gray-500 mb-1">Status</span>
                    {getStatusBadge(viewLead)}
                  </div>
                  {viewLead.crm_platform && (
                    <div className="text-right">
                      <span className="block text-xs text-gray-500 mb-1">Platform</span>
                      <span className="inline-flex items-center px-2 py-0.5  text-xs font-bold bg-indigo-100 text-indigo-800">
                        {viewLead.crm_platform}
                      </span>
                    </div>
                  )}
                </div>

                {viewLead.crm_lead_id && (
                  <div>
                    <span className="block text-xs text-gray-500">External CRM ID</span>
                    <code className="text-xs bg-gray-100 p-1  border border-gray-200 block mt-1">
                      {viewLead.crm_lead_id}
                    </code>
                  </div>
                )}

                {viewLead.last_synced_at && (
                  <div>
                    <span className="block text-xs text-gray-500">Last Sync Attempt</span>
                    <span className="block text-sm text-gray-900">
                      {new Date(viewLead.last_synced_at).toLocaleString()}
                    </span>
                  </div>
                )}

                {viewLead.error_message && (
                  <div className="bg-red-50 border border-red-100 p-3  text-sm text-red-700">
                    <span className="font-medium text-red-800 mb-1 text-xs uppercase inline-flex items-center gap-1.5">
                      Sync Error
                      {isHubspotLeadSourceError(viewLead) && <HubspotLeadSourceHelp />}
                    </span>
                    {viewLead.error_message}
                    {isHubspotLeadSourceError(viewLead) && (
                      <ol className="list-decimal pl-4 mt-2 space-y-1 text-red-800">
                        {HUBSPOT_LEAD_SOURCE_HELP_STEPS.map((step) => (
                          <li key={`modal-${step}`}>{step}</li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Lead"
        footer={
          <>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center  border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex w-full justify-center  border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleSaveEdit}
            >
              Save Changes
            </button>
          </>
        }
      >
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstname" className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                id="firstname"
                className="mt-1 block w-full  border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                value={editFormData.firstname || ''}
                onChange={(e) => setEditFormData({ ...editFormData, firstname: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="lastname" className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                id="lastname"
                className="mt-1 block w-full  border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                value={editFormData.lastname || ''}
                onChange={(e) => setEditFormData({ ...editFormData, lastname: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              id="email"
              className="mt-1 block w-full  border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              value={editFormData.email || ''}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="text"
              id="phone"
              className="mt-1 block w-full  border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              value={editFormData.phone || ''}
              onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700">Company</label>
            <input
              type="text"
              id="company"
              className="mt-1 block w-full  border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              value={editFormData.company || ''}
              onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!leadToDelete}
        onClose={() => setLeadToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Lead"
        message={`Are you sure you want to delete ${leadToDelete?.firstname || ''} ${leadToDelete?.lastname || leadToDelete?.email}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}
